import React, { useMemo } from 'react';
import compact from 'lodash/compact';
import uniq from 'lodash/uniq';
import Head from 'next/head';
import parse from 'node-html-parser';

import { useCatalogProductIdParams } from '../hooks/useCatalogProductIdParams';
import { getFileExtension, modifyQueryParams } from '../lib/utils';
import { usePdpBaseInfo } from '../services/usePdpBaseInfo';
import { useProductThumbnail } from '../services/useProductThumbnail';

export const PreloadHead = () => {
  const param = useCatalogProductIdParams();
  const {
    product: { description },
  } = usePdpBaseInfo(param);
  const { image_list: imageList } = useProductThumbnail();

  const preload_first_images = useMemo(() => {
    try {
      let origin_image: string | null = imageList[0].static_url ? imageList[0].static_url : imageList[0].url || '';
      let webp_image: string | null = null;
      let low_quality_image: string | null = modifyQueryParams(origin_image, {
        width: '400',
        height: '400',
        quality: '80',
      });
      const is_gif = getFileExtension(low_quality_image) === 'gif';
      let low_quality_image_webp: string | null = null;

      if (!is_gif) {
        webp_image = modifyQueryParams(origin_image, {
          format: 'webp',
        });
        low_quality_image_webp = modifyQueryParams(low_quality_image, {
          format: 'webp',
        });
        low_quality_image = null;
        origin_image = null;
      }

      return compact(uniq([low_quality_image_webp, low_quality_image, origin_image, webp_image]));
    } catch (err) {
      return null;
    }
  }, [imageList]);

  const description_resource_hints = useMemo(() => {
    try {
      const img_tags = parse(description as string).querySelectorAll('img');
      const images = img_tags.map((img_tag) => img_tag.getAttribute('src'));

      return {
        preconnects: uniq(images.map((image) => new URL(image as string).origin)),
      };
    } catch (err) {
      return null;
    }
  }, [description]);

  return (
    <Head>
      <link rel='preconnect' href='https://cf.product-image.s.zigzag.kr' />
      {preload_first_images &&
        preload_first_images.map((image, index) => (
          <link key={`preload-first-image-${index}`} rel='preload' as='image' href={image} />
        ))}
      {description_resource_hints && (
        <>
          {description_resource_hints.preconnects.map((preconnect, index) => (
            <link key={`description-preconnect-${index}`} rel='preconnect' href={preconnect} />
          ))}
        </>
      )}
      <link rel='preconnect' href='https://cf.product-video.s.zigzag.kr' />
    </Head>
  );
};
